/**
 * ProposalMarker — the small collapsed pill rendered INLINE in the
 * AoA chat in place of the full ProposalCard. Acts as a click-through
 * to surface the full card in the side inspector panel.
 *
 * Visual design (Claude Code style):
 *   - A 1-line pill with a status-coloured left dot
 *   - Tool label + entity name (e.g. "edit agent · JewishPro")
 *   - A status chip on the right (pending / applied / rejected)
 *   - Subtle hover state — entire pill is a button
 *
 * Click behaviour:
 *   - For a PENDING proposal: opens the card in the inspector panel.
 *     If the panel is collapsed it auto-expands.
 *   - For a RESOLVED proposal: opens a read-only view in the panel
 *     (the user can review what they decided without re-firing it).
 */

import React from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { useInspector } from './InspectorContext';
import { statusLabelOf, toolLabelOf, fieldLabelOf } from '../proposalLabels';
import type { AoAProposal } from '../types';
import './ProposalMarker.css';

function statusClass(status: string): string {
  if (status === 'approved' || status === 'edited') return 'is-applied';
  if (status === 'rejected' || status === 'timeout') return 'is-rejected';
  return 'is-pending';
}

interface Props {
  proposal: AoAProposal;
  /** When this proposal is the active one in the panel, we render the
   *  marker with a subtle "open" outline so the user can spot which
   *  marker the panel is currently displaying. */
  isActiveInPanel?: boolean;
}

export const ProposalMarker: React.FC<Props> = ({ proposal, isActiveInPanel }) => {
  const { t } = useLanguage();
  const { setActiveProposal, setCollapsed } = useInspector();

  const handleClick = () => {
    setActiveProposal(proposal);
    // Always make sure the panel is visible when the user explicitly
    // clicks a marker — the click intent is "show me this".
    setCollapsed(false);
  };

  const label = toolLabelOf(t, proposal.tool_name);
  const status = proposal.status;
  const sLabel = statusLabelOf(t, status);
  const sClass = statusClass(status);

  // Try to surface a useful identifier — the target entity name when
  // we have it in the proposed_payload, falling back to the field name
  // for edits or the entity id as a last resort.
  const payload = (proposal.proposed_payload || {}) as Record<string, any>;
  const targetName =
    payload.target_name ||
    payload.chatbot_name ||
    payload.skill_name ||
    payload.bundle_name ||
    payload.agent_name ||
    null;
  // fieldLabelOf, not the raw column name: the proposal CARD already
  // resolves this and the marker showed "content" instead of "Content".
  const fieldName = proposal.field ? fieldLabelOf(t, proposal.field) : null;
  // The target's name and the field are DIFFERENT facts. "Repro Alpha · Model"
  // is what tells four model cards in one build apart; the field alone stood
  // in for the name only because edit cards did not carry one. Both show when
  // both are known.
  const entityName = targetName || fieldName;
  const fieldChip = targetName && fieldName ? fieldName : null;

  return (
    <button
      type="button"
      className={[
        'aoa-proposal-marker',
        sClass,
        isActiveInPanel ? 'is-active-in-panel' : '',
      ].filter(Boolean).join(' ')}
      onClick={handleClick}
      aria-label={t('metaAgent.proposal.openCardAria', { label })}
      data-testid={`proposal-marker-${proposal.proposal_id}`}
    >
      <span className={`aoa-proposal-marker__dot ${sClass}`} aria-hidden="true" />
      <span className="aoa-proposal-marker__label">{label}</span>
      {entityName && (
        <>
          <span className="aoa-proposal-marker__sep" aria-hidden="true">·</span>
          <span dir="auto" className="aoa-proposal-marker__entity">{entityName}</span>
        </>
      )}
      {fieldChip && (
        <>
          <span className="aoa-proposal-marker__sep" aria-hidden="true">·</span>
          <span className="aoa-proposal-marker__entity aoa-proposal-marker__field">{fieldChip}</span>
        </>
      )}
      <span className={`aoa-proposal-marker__status ${sClass}`}>
        {sLabel}
      </span>
      <svg
        className="aoa-proposal-marker__arrow"
        width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M7 17L17 7" />
        <path d="M9 7h8v8" />
      </svg>
    </button>
  );
};
